import React from 'react'

import { Tag } from '../../components'
import { useTableSort } from '../../components/Table'
import { ExternalLink } from '../../components/external-link'
import { FinancialsWidget, getMoMPercentageData, useFinancialsTableContext } from '../../components/financials-table'
import { LineStackedBarChart } from '../../components/line-stacked-bar-chart'
import { TrafficTableBottomContent } from './TrafficTableBottomContent'
import { TrafficTableHead } from './TrafficTableHead'
import { WidgetAppSettingsSection } from './WidgetAppSettingsSection'
import { AppPlatformLabels, AppRegionLabels, DEFAULT_SHOW_NUM_OF_APPS, SEGMENT_COLORS } from './constants'
import { ParsedAppItem, TrafficRowDataType, TrafficSettings, TrafficSubRowDataType } from './types'
import { MonthlyDataRowSelectionOverridesType, useMonthlyDataControls } from './useMonthlyDataControls'
import { useTableColumns } from './useTableColumns'
import { getAppStoreLink, getSelectedAppsArray } from './utils'

interface MapDataProps {
  domain: string | null
  selectedAppsArray: ParsedAppItem[]
  selectionOverrides?: MonthlyDataRowSelectionOverridesType
  yearKeys: number[]
  isPlaceholder: boolean
}

function mapData({
  domain,
  selectedAppsArray,
  selectionOverrides,
  yearKeys,
  isPlaceholder,
}: MapDataProps): TrafficRowDataType[] {
  return selectedAppsArray.map((app, index) => {
    const appName = app.appName ?? 'N/A'
    const platformLabel = app.os ? AppPlatformLabels[app.os] : 'N/A'
    const geoLabel = app.geo ? AppRegionLabels[app.geo] : 'N/A'
    const values = !isPlaceholder
      ? yearKeys.map((date) => {
          const index = app.mau?.d.findIndex((d) => d === date) ?? -1
          return {
            year: date.toString(),
            value: app.mau?.v[index] ?? null,
          }
        })
      : []

    const valuesWithMomData: TrafficSubRowDataType['values'] = []
    const keyValueMap: TrafficSubRowDataType['keyValueMap'] = {}
    const momKeyValueMap: TrafficSubRowDataType['momKeyValueMap'] = {}

    if (!isPlaceholder) {
      const momData = getMoMPercentageData(values)
      values.forEach(({ year, value }) => {
        const momValue = momData[year] ?? null
        keyValueMap[year] = value
        momKeyValueMap[year] = momValue

        valuesWithMomData.push({
          year,
          value,
          momValue,
        })
      })
    }

    const isHighlighted = app.domain === domain

    return {
      id: app.id,
      label: [appName, platformLabel, geoLabel].join(' - '),
      labelColumnClassName: 'whitespace-nowrap',
      isLabelColumnSticky: true,
      labelElement: (
        <div className="h-4 overflow-hidden text-ellipsis whitespace-nowrap">
          <ExternalLink label={appName} href={getAppStoreLink(app)} isBold={isHighlighted} />
          <Tag size="small">{platformLabel}</Tag> <Tag size="small">{geoLabel}</Tag>
        </div>
      ),
      isHighlighted,
      geo: app.geo,
      os: app.os,
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
      isSelected: selectionOverrides?.[app.id] ?? index < DEFAULT_SHOW_NUM_OF_APPS,
      values: valuesWithMomData,
      keyValueMap,
      momKeyValueMap,
    }
  })
}

interface Props {
  domainApps: ParsedAppItem[]
  peerApps: ParsedAppItem[]
  industryApps: ParsedAppItem[]
  hasBeenInView: boolean
  isInView: boolean
}

export function Apps({ domainApps, peerApps, industryApps, hasBeenInView, isInView }: Props) {
  const { settings } = useFinancialsTableContext<TrafficSettings>()
  const { domain, yearKeys, stringYearKeys } = React.useMemo(() => {
    const domain = domainApps?.[0].domain ?? null
    const domainAppsYears = domainApps?.flatMap(({ mau }) => mau?.d) ?? []
    const peerAppsYears = peerApps?.flatMap(({ mau }) => mau?.d) ?? []
    const industryAppsYears = industryApps?.flatMap(({ mau }) => mau?.d) ?? []

    const yearKeys = (
      [...new Set([...domainAppsYears, ...peerAppsYears, ...industryAppsYears])].filter(Boolean) as number[]
    ).sort((a, b) => a - b)

    return {
      yearKeys,
      stringYearKeys: yearKeys.map((y) => y.toString()),
      domain,
    }
  }, [domainApps, peerApps, industryApps])

  const selectedAppsArray = React.useMemo(
    () =>
      getSelectedAppsArray({
        domainApps,
        peerApps,
        industryApps,
        appPlatforms: settings.appPlatforms,
        appRegions: settings.appRegions,
      }),
    [domainApps, peerApps, industryApps, settings.appRegions, settings.appPlatforms]
  )
  const clearInactiveIdsBlacklist = React.useMemo(
    () => selectedAppsArray.slice(0, DEFAULT_SHOW_NUM_OF_APPS).map(({ id }) => id),
    [selectedAppsArray]
  )
  const { monthlyDataRowSelectionOverrides, toggleMonthlyDataRowSelection, isShowMore, toggleIsShowMore } =
    useMonthlyDataControls({ clearInactiveIdsBlacklist })
  const [monthlyData, setMonthlyData] = React.useState(() =>
    mapData({
      domain,
      selectedAppsArray: selectedAppsArray.slice(0, DEFAULT_SHOW_NUM_OF_APPS),
      yearKeys,
      isPlaceholder: true,
    })
  )
  const { columns, valueGetter } = useTableColumns(stringYearKeys, settings.trafficDataView, hasBeenInView)

  const {
    data: sortedData,
    sort,
    changeSort,
  } = useTableSort({
    data: monthlyData,
    columns,
    defaultSort: { column: columns[columns.length - 1], direction: -1 },
    key: hasBeenInView ? 1 : 0,
    valueGetter,
  })

  React.useEffect(() => {
    if (!hasBeenInView) {
      return
    }

    const newMonthlyData = mapData({
      domain,
      selectedAppsArray: selectedAppsArray.filter(
        ({ id }, index) =>
          isShowMore || index < DEFAULT_SHOW_NUM_OF_APPS || monthlyDataRowSelectionOverrides[id] !== undefined
      ),
      yearKeys,
      selectionOverrides: monthlyDataRowSelectionOverrides,
      isPlaceholder: false,
    })

    setMonthlyData(newMonthlyData)
  }, [domain, selectedAppsArray, monthlyDataRowSelectionOverrides, yearKeys, isShowMore, hasBeenInView])

  const isEmpty = monthlyData.length === 0

  return (
    <div className="fv-container has-responsive-padding pb-8 pt-4">
      <FinancialsWidget
        data={sortedData}
        header={<WidgetAppSettingsSection />}
        tableHead={
          isEmpty ? undefined : (
            <TrafficTableHead
              stickyLabelWidthClassName="w-80"
              columns={columns}
              sort={sort}
              onSortChange={changeSort}
            />
          )
        }
        columns={columns}
        hasStickyColumnWrapper={hasBeenInView}
        onVisibilityChange={toggleMonthlyDataRowSelection}
      >
        <LineStackedBarChart
          data={sortedData}
          yearKeys={stringYearKeys}
          chartView={settings.chartView}
          lineChartTooltipSort="value"
          emptyMessage={
            <>
              No matching Apps found.
              <br />
              Adjust your filters for more results.
            </>
          }
          isEmpty={isEmpty}
          isInView={isInView}
          hasBeenInView={hasBeenInView}
          isFullWidth
          hasXAxis
        />
      </FinancialsWidget>
      {!isEmpty && (
        <TrafficTableBottomContent
          showMoreLabel="Expand List of Similar Apps"
          showLessLabel="Collapse List of Similar Apps"
          hasHiddenRows={selectedAppsArray.length > DEFAULT_SHOW_NUM_OF_APPS}
          isShowMore={isShowMore}
          onShowMoreToggle={toggleIsShowMore}
        />
      )}
    </div>
  )
}
